import { ChevronDown, ChevronRight, ListOrdered, Plus } from 'lucide-react';
import React, { useCallback, useEffect, useState } from 'react';
import { listEscalationSteps, type EscalationStepRow } from '../api';
import { EmptyState } from '../components/EmptyState';
import { SectionCard } from '../components/SectionCard';
import { useSession } from '../contexts/SessionContext';
import { useWorkspace } from '../contexts/WorkspaceContext';
import type { UseActionDialogReturn } from '../hooks/useActionDialog';

type EscalationPoliciesPageProps = {
  openDialog: UseActionDialogReturn['openDialog'];
};

function EscalationPoliciesPage({ openDialog }: EscalationPoliciesPageProps) {
  const { session } = useSession();
  const { workspace, activeOrganization, workspaceVersion } = useWorkspace();
  const { escalationPolicies } = workspace;

  const [expandedPolicyId, setExpandedPolicyId] = useState('');
  const [stepsByPolicyId, setStepsByPolicyId] = useState<Record<string, EscalationStepRow[]>>({});
  const [stepsLoading, setStepsLoading] = useState(false);

  const loadSteps = useCallback(async (escalationPolicyId: string) => {
    if (!session) return;
    setStepsLoading(true);
    try {
      const steps = await listEscalationSteps(session.accessToken, escalationPolicyId);
      setStepsByPolicyId((previous) => ({
        ...previous,
        [escalationPolicyId]: [...steps].sort((a, b) => a.stepOrder - b.stepOrder)
      }));
    } catch {
      setStepsByPolicyId((previous) => ({ ...previous, [escalationPolicyId]: [] }));
    } finally {
      setStepsLoading(false);
    }
  }, [session]);

  useEffect(() => {
    if (expandedPolicyId) {
      void loadSteps(expandedPolicyId);
    }
  }, [expandedPolicyId, workspaceVersion, loadSteps]);

  function handleCreatePolicy() {
    if (!activeOrganization) return;
    openDialog({
      kind: 'escalation-policy',
      organizationId: activeOrganization.id,
      teamHandle: '',
      appliesToCategory: ''
    });
  }

  function handleAddStep(policy: (typeof escalationPolicies)[number]) {
    const existingSteps = stepsByPolicyId[policy.escalationPolicyId] || [];
    const nextOrder = existingSteps.length > 0
      ? Math.max(...existingSteps.map((step) => step.stepOrder)) + 1
      : 1;
    openDialog({
      kind: 'escalation-step',
      escalationPolicyId: policy.escalationPolicyId,
      policyLabel: policy.appliesToCategory
        ? `${policy.teamHandle} · ${policy.appliesToCategory}`
        : policy.teamHandle,
      stepOrder: String(nextOrder),
      channel: 'in_app',
      timeoutSeconds: '300',
      targetType: 'on_call',
      targetWorkIdentityId: ''
    });
  }

  return (
    <SectionCard
      label="On-call"
      title="Escalation policies"
      description="Ordered notification steps run when an alert enters an escalation policy."
      action={
        <button type="button" className="primaryButton" onClick={handleCreatePolicy}>
          <Plus size={14} />
          <span>New policy</span>
        </button>
      }
    >
      {escalationPolicies.length > 0 ? (
        <div className="releaseTableWrap" role="region" aria-label="Escalation policies table" tabIndex={0}>
          <table className="releaseTable">
            <thead>
              <tr>
                <th aria-label="Expand" />
                <th>Team</th>
                <th>Category</th>
                <th aria-label="Actions" />
              </tr>
            </thead>
            <tbody>
              {escalationPolicies.map((policy) => {
                const expanded = expandedPolicyId === policy.escalationPolicyId;
                const steps = stepsByPolicyId[policy.escalationPolicyId] || [];
                return (
                  <React.Fragment key={policy.escalationPolicyId}>
                    <tr>
                      <td>
                        <button
                          type="button"
                          className="iconButton"
                          aria-label={expanded ? 'Collapse steps' : 'Expand steps'}
                          onClick={() => setExpandedPolicyId(expanded ? '' : policy.escalationPolicyId)}
                        >
                          {expanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                        </button>
                      </td>
                      <td><strong>{policy.teamHandle}</strong></td>
                      <td>{policy.appliesToCategory || <span className="recoveryUnavailable">Default</span>}</td>
                      <td className="releaseTableAction">
                        <button type="button" className="secondaryButton" onClick={() => handleAddStep(policy)}>
                          Add step
                        </button>
                      </td>
                    </tr>
                    {expanded ? (
                      <tr>
                        <td />
                        <td colSpan={3}>
                          {stepsLoading && steps.length === 0 ? (
                            <div style={{ padding: '8px 0', color: 'var(--text-faint)', fontSize: 13 }}>
                              Loading steps…
                            </div>
                          ) : steps.length === 0 ? (
                            <EmptyState
                              compact
                              icon={<ListOrdered size={16} />}
                              title="No steps yet"
                              description="Add a step to define who gets notified first."
                            />
                          ) : (
                            <ol style={{ margin: 0, paddingLeft: 18, fontSize: 13, lineHeight: 1.8 }}>
                              {steps.map((step) => (
                                <li key={step.escalationStepId}>
                                  <strong>{step.channel === 'in_app' ? 'In-app' : 'Email'}</strong>
                                  {' → '}
                                  {step.targetType === 'on_call' ? 'whoever is on call' : `identity ${step.targetWorkIdentityId}`}
                                  {' · timeout '}
                                  {step.timeoutSeconds}s
                                </li>
                              ))}
                            </ol>
                          )}
                        </td>
                      </tr>
                    ) : null}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : (
        <EmptyState
          icon={<ListOrdered size={20} />}
          title="No escalation policies yet"
          description="Create a policy to define who gets notified, and in what order, for a team."
          action={
            <button type="button" className="primaryButton" onClick={handleCreatePolicy}>
              <Plus size={14} />
              <span>New policy</span>
            </button>
          }
        />
      )}
    </SectionCard>
  );
}

export { EscalationPoliciesPage };
